<template>
  <ComingSoon v-if="!isAllowedDomain" />
  <div v-else class="zi-page">
    <!-- History sidebar -->
    <div class="zi-sidebar" :class="{ 'zi-sidebar-open': sidebarOpen }">
      <div class="zi-sidebar-header">
        <b-button variant="primary" block size="sm" @click="startNewConversation">
          <feather-icon icon="PlusIcon" class="mr-50" />
          New conversation
        </b-button>
      </div>
      <div class="zi-conversation-list">
        <div
          v-for="conv in conversations"
          :key="conv.id"
          class="zi-conversation-item"
          :class="{ 'zi-active': conv.id === activeConversationId }"
          @click="loadConversation(conv.id)"
        >
          <feather-icon icon="MessageSquareIcon" size="14" class="mr-50 flex-shrink-0" />
          <span class="text-truncate zi-conversation-preview">{{ conv.preview }}</span>
          <button
            class="zi-delete-btn flex-shrink-0"
            title="Delete conversation"
            @click.stop="deleteConversation(conv)"
          >
            <feather-icon icon="Trash2Icon" size="13" />
          </button>
        </div>
        <div v-if="!conversationsLoading && conversations.length === 0" class="zi-empty-history">
          No past conversations yet
        </div>
      </div>
    </div>

    <div v-if="sidebarOpen" class="zi-sidebar-backdrop d-lg-none" @click="sidebarOpen = false" />

    <!-- Main panel -->
    <div class="zi-main">
      <div class="zi-header">
        <b-button
          variant="flat-secondary"
          size="sm"
          class="d-lg-none mr-50 p-50"
          @click="sidebarOpen = !sidebarOpen"
        >
          <feather-icon icon="MenuIcon" />
        </b-button>
        <img :src="assistantIcon" class="zi-header-icon" alt="" />
        <span class="zi-header-title">Ask Zircly AI</span>
      </div>

      <div class="zi-transcript" ref="transcript">
        <div v-if="messages.length === 0 && !isBusy" class="zi-empty-state">
          <img :src="assistantIcon" class="zi-empty-icon" alt="" />
          <h5>Ask Zircly AI anything</h5>
          <p class="text-muted">
            Company policies, recent posts, or your own info — try one of these:
          </p>
          <div class="zi-example-questions">
            <b-button
              v-for="(example, idx) in exampleQuestions"
              :key="idx"
              variant="outline-secondary"
              size="sm"
              class="zi-example-btn"
              @click="askExample(example)"
            >
              {{ example }}
            </b-button>
          </div>
        </div>

        <div v-for="msg in messages" :key="msg.id" class="zi-message-row" :class="`zi-role-${msg.role}`">
          <b-avatar
            size="32"
            class="zi-avatar flex-shrink-0"
            :class="msg.role === 'user' ? 'zi-avatar-user' : 'zi-avatar-assistant'"
          >
            <feather-icon v-if="msg.role === 'user'" icon="UserIcon" size="16" />
            <img v-else :src="assistantIcon" class="zi-avatar-bot-icon" alt="" />
          </b-avatar>

          <div class="zi-bubble-wrap">
            <div
              class="zi-bubble"
              :class="{
                'zi-bubble-user': msg.role === 'user',
                'zi-bubble-assistant': msg.role === 'assistant' && !msg.isDeclined,
                'zi-bubble-declined': msg.isDeclined,
              }"
            >
              <div v-if="msg.role === 'assistant'" v-html="renderMarkdown(msg.content)" />
              <div v-else class="zi-plain-text">{{ msg.content }}</div>

              <div v-if="msg.citations" class="zi-citations">
                <feather-icon icon="LinkIcon" size="11" class="mr-25" />
                Based on: {{ msg.citations }}
              </div>
            </div>

            <div v-if="msg.role === 'assistant' && !msg.streaming" class="zi-message-actions">
              <button class="zi-action-btn" title="Copy" @click="copyMessage(msg)">
                <feather-icon :icon="msg.copied ? 'CheckIcon' : 'CopyIcon'" size="14" />
              </button>
              <button
                class="zi-action-btn zi-like-btn"
                :class="{ 'zi-action-active': msg.feedback === 'like' }"
                title="Good answer"
                @click="sendFeedback(msg, 'like')"
              >
                <feather-icon icon="ThumbsUpIcon" size="14" />
              </button>
              <button
                class="zi-action-btn zi-dislike-btn"
                :class="{ 'zi-action-active': msg.feedback === 'dislike' }"
                title="Bad answer"
                @click="sendFeedback(msg, 'dislike')"
              >
                <feather-icon icon="ThumbsDownIcon" size="14" />
              </button>
            </div>
          </div>
        </div>

        <div v-if="currentStageLabel" class="zi-message-row zi-role-assistant">
          <b-avatar size="32" class="zi-avatar zi-avatar-assistant flex-shrink-0">
            <img :src="assistantIcon" class="zi-avatar-bot-icon" alt="" />
          </b-avatar>
          <div class="zi-bubble-wrap">
            <div class="zi-bubble zi-bubble-stage">
              <span class="zi-stage-dot" /><span class="zi-stage-dot" /><span class="zi-stage-dot" />
              {{ currentStageLabel }}
            </div>
          </div>
        </div>

        <div v-if="streamError" class="zi-message-row zi-role-assistant">
          <b-avatar size="32" class="zi-avatar zi-avatar-error flex-shrink-0">
            <feather-icon icon="AlertTriangleIcon" size="16" />
          </b-avatar>
          <div class="zi-bubble-wrap">
            <div class="zi-bubble zi-bubble-error">
              {{ streamError }}
              <b-link class="ml-50" @click="retryLast">Retry</b-link>
            </div>
          </div>
        </div>
      </div>

      <div class="zi-input-area">
        <b-form-textarea
          v-model="question"
          placeholder="Ask Zircly AI... (Enter to send, Shift+Enter for newline)"
          rows="1"
          max-rows="6"
          :maxlength="MAX_LENGTH"
          :disabled="isBusy"
          class="zi-input"
          @keydown.native="handleKeydown"
        />
        <div class="zi-input-footer">
          <span v-if="question.length > WARN_THRESHOLD" class="zi-char-counter" :class="{ 'zi-char-limit': question.length >= MAX_LENGTH }">
            {{ question.length }}/{{ MAX_LENGTH }}
          </span>
          <b-button
            variant="primary"
            size="sm"
            class="zi-send-btn"
            :disabled="isBusy || !question.trim()"
            @click="sendQuestion"
          >
            <b-spinner v-if="isBusy" small class="mr-50" />
            Send
          </b-button>
        </div>
        <div class="zi-disclaimer">Zircly AI can make mistakes. Please double-check important information.</div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BAvatar, BFormTextarea, BSpinner, BLink } from "bootstrap-vue";
import axios from "@axios";
import { marked } from "marked";
import DOMPurify from "dompurify";
import Vue from "vue";
import VueCookies from "vue-cookies";
import ComingSoon from "./ComingSoon.vue";
import { isChatbotDomainAllowed } from "@/utils/chatbot-domain";
Vue.use(VueCookies);

const MAX_LENGTH = 1000;
const WARN_THRESHOLD = 800;

export default {
  components: { BButton, BAvatar, BFormTextarea, BSpinner, BLink, ComingSoon },
  data() {
    return {
      isAllowedDomain: isChatbotDomainAllowed(),
      MAX_LENGTH,
      WARN_THRESHOLD,
      assistantIcon: require("@/assets/images/icons/zircly_slack_icon.png"),
      question: "",
      messages: [],
      conversations: [],
      conversationsLoading: false,
      activeConversationId: null,
      isBusy: false,
      currentStageLabel: "",
      streamError: "",
      sidebarOpen: false,
      lastQuestion: "",
      nextLocalId: 1,
      exampleQuestions: [
        "What is the holiday policy?",
        "What did the company post recently?",
        "What is my phone number on file?",
      ],
    };
  },
  created() {
    if (this.isAllowedDomain) {
      this.loadConversations();
    }
  },
  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.transcript;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    renderMarkdown(text) {
      const html = marked.parse(text || "", { breaks: true });
      return DOMPurify.sanitize(html);
    },
    formatCitations(sources) {
      if (!sources || !sources.length) return "";
      const seen = new Set();
      const labels = [];
      sources.forEach((s) => {
        const label = s.name || s.title;
        if (label && !seen.has(label)) {
          seen.add(label);
          labels.push(label);
        }
      });
      return labels.join(", ");
    },
    handleKeydown(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.sendQuestion();
      }
    },
    askExample(text) {
      this.question = text;
      this.sendQuestion();
    },
    startNewConversation() {
      this.activeConversationId = null;
      this.messages = [];
      this.streamError = "";
      this.sidebarOpen = false;
    },
    async loadConversations() {
      this.conversationsLoading = true;
      try {
        const { data } = await axios.get("/chatbot/conversations");
        this.conversations = data.data;
      } catch (e) {
        // non-fatal — history sidebar just stays empty
      } finally {
        this.conversationsLoading = false;
      }
    },
    async loadConversation(id) {
      this.activeConversationId = id;
      this.streamError = "";
      this.sidebarOpen = false;
      try {
        const { data } = await axios.get(`/chatbot/conversations/${id}/messages`);
        this.messages = data.data.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          isDeclined: !!m.is_declined,
          citations: this.formatCitations(m.sources_json),
          feedback: m.feedback_type || null,
          copied: false,
          streaming: false,
        }));
        this.scrollToBottom();
      } catch (e) {
        this.streamError = "Couldn't load that conversation.";
      }
    },
    async deleteConversation(conv) {
      const confirmed = window.confirm(
        `Delete this conversation? ("${conv.preview}")`
      );
      if (!confirmed) return;

      try {
        await axios.delete(`/chatbot/conversations/${conv.id}`);
        this.conversations = this.conversations.filter((c) => c.id !== conv.id);

        if (this.activeConversationId === conv.id) {
          this.startNewConversation();
        }
      } catch (e) {
        // best-effort — leave the item in the list if the delete failed
      }
    },
    copyMessage(msg) {
      navigator.clipboard.writeText(msg.content).then(() => {
        msg.copied = true;
        setTimeout(() => {
          msg.copied = false;
        }, 1500);
      });
    },
    async sendFeedback(msg, type) {
      const next = msg.feedback === type ? type : type;
      msg.feedback = next;
      try {
        await axios.post(`/chatbot/messages/${msg.id}/feedback`, { feedback_type: type });
      } catch (e) {
        // best-effort — feedback isn't critical path, leave selection as-is
      }
    },
    retryLast() {
      this.streamError = "";
      this.question = this.lastQuestion;
      this.sendQuestion();
    },
    async sendQuestion() {
      const questionText = this.question.trim();
      if (!questionText || this.isBusy) return;

      this.lastQuestion = questionText;
      this.streamError = "";
      this.messages.push({
        id: `local-${this.nextLocalId++}`,
        role: "user",
        content: questionText,
        isDeclined: false,
        citations: "",
        feedback: null,
        copied: false,
        streaming: false,
      });
      this.question = "";
      this.$nextTick(() => {
        // BFormTextarea doesn't reliably reflect a v-model reset back into
        // the native <textarea> when the reset happens synchronously inside
        // a .native keydown handler (confirmed: the Vue data clears
        // correctly, but the DOM value doesn't follow) — nudge it again
        // after Vue's own DOM patch cycle to force the sync.
        this.question = "";
      });
      this.isBusy = true;
      this.currentStageLabel = "";
      this.scrollToBottom();

      const assistantMsg = {
        id: `local-${this.nextLocalId++}`,
        role: "assistant",
        content: "",
        isDeclined: false,
        citations: "",
        feedback: null,
        copied: false,
        streaming: true,
      };

      try {
        const token = this.$cookies.get("accessToken");
        const baseUrl = process.env.VUE_APP_API_URL;

        const response = await fetch(`${baseUrl}/chatbot/ask/stream`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question: questionText,
            conversation_id: this.activeConversationId,
          }),
        });

        if (!response.ok || !response.body) {
          throw new Error("stream request failed");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let pushedAssistant = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          let boundary;

          while ((boundary = buffer.indexOf("\n\n")) !== -1) {
            const rawEvent = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);

            const eventMatch = rawEvent.match(/^event: (.+)$/m);
            const dataMatch = rawEvent.match(/^data: (.+)$/m);
            if (!eventMatch || !dataMatch) continue;

            const eventType = eventMatch[1];
            const data = JSON.parse(dataMatch[1]);

            if (eventType === "stage") {
              this.currentStageLabel = data.label;
            } else if (eventType === "token") {
              if (!pushedAssistant) {
                this.messages.push(assistantMsg);
                pushedAssistant = true;
                this.currentStageLabel = "";
              }
              assistantMsg.content += data.delta;
              this.scrollToBottom();
            } else if (eventType === "done") {
              this.currentStageLabel = "";
              assistantMsg.streaming = false;
              assistantMsg.id = data.message_id;
              assistantMsg.isDeclined = !!data.is_declined;
              assistantMsg.citations = this.formatCitations(data.sources);

              if (!this.activeConversationId) {
                this.activeConversationId = data.conversation_id;
                this.loadConversations();
              }
            } else if (eventType === "error") {
              this.currentStageLabel = "";
              this.streamError = data.message;
              if (pushedAssistant) {
                this.messages = this.messages.filter((m) => m !== assistantMsg);
              }
            }
          }
        }
      } catch (e) {
        this.currentStageLabel = "";
        this.streamError = "Connection interrupted — please try again.";
      } finally {
        this.isBusy = false;
        this.scrollToBottom();
      }
    },
  },
};
</script>

<style scoped>
.zi-page {
  display: flex;
  height: calc(100vh - 140px);
  min-height: 500px;
  background: var(--white, #fff);
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.zi-sidebar {
  width: 260px;
  flex-shrink: 0;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  background: #fafafc;
}

.zi-sidebar-header {
  padding: 0.85rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.zi-conversation-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.4rem;
}

.zi-conversation-item {
  display: flex;
  align-items: center;
  padding: 0.55rem 0.6rem;
  border-radius: 0.4rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: #5e5873;
  margin-bottom: 2px;
}

.zi-conversation-item:hover {
  background: rgba(56, 178, 45, 0.08);
}

.zi-conversation-item.zi-active {
  background: rgba(56, 178, 45, 0.14);
  color: var(--primary, #38B22D);
  font-weight: 500;
}

.zi-conversation-preview {
  flex: 1;
  min-width: 0;
}

.zi-delete-btn {
  border: none;
  background: transparent;
  color: #b9b9c3;
  padding: 0.15rem 0.3rem;
  border-radius: 0.3rem;
  display: none;
  align-items: center;
  margin-left: 0.3rem;
}

.zi-conversation-item:hover .zi-delete-btn {
  display: flex;
}

.zi-delete-btn:hover {
  background: rgba(234, 84, 85, 0.12);
  color: #ea5455;
}

.zi-empty-history {
  padding: 1rem 0.75rem;
  font-size: 0.8rem;
  color: #b9b9c3;
}

.zi-sidebar-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10;
}

.zi-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.zi-header {
  display: flex;
  align-items: center;
  padding: 0.9rem 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.zi-header-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  margin-right: 0.6rem;
  object-fit: contain;
}

.zi-header-title {
  font-weight: 600;
  font-size: 1.05rem;
}

.zi-transcript {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.zi-empty-state {
  text-align: center;
  max-width: 480px;
  margin: 2.5rem auto;
}

.zi-empty-icon {
  width: 48px;
  height: 48px;
  object-fit: contain;
  margin-bottom: 0.75rem;
  opacity: 0.85;
}

.zi-example-questions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.75rem;
}

.zi-example-btn {
  border-radius: 999px;
}

.zi-message-row {
  display: flex;
  margin-bottom: 1.25rem;
  align-items: flex-start;
}

.zi-role-user {
  flex-direction: row-reverse;
}

.zi-role-user .zi-bubble-wrap {
  align-items: flex-end;
}

.zi-avatar {
  margin: 0 0.65rem;
}

/* bootstrap-vue's badge-* variant classes resolve to a transparent
   background in this theme, leaving a white-on-white icon — set the
   avatar colors explicitly instead of relying on the variant prop. */
.zi-avatar-user ::v-deep .b-avatar-custom,
.zi-avatar-user {
  background-color: var(--primary, #38B22D) !important;
  color: #fff !important;
}

.zi-avatar-assistant ::v-deep .b-avatar-custom,
.zi-avatar-assistant {
  background-color: #fff !important;
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #fff !important;
  overflow: hidden;
}

.zi-avatar-bot-icon {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.zi-avatar-error ::v-deep .b-avatar-custom,
.zi-avatar-error {
  background-color: #ea5455 !important;
  color: #fff !important;
}

.zi-bubble-wrap {
  display: flex;
  flex-direction: column;
  max-width: 72%;
}

.zi-bubble {
  padding: 0.65rem 1rem;
  border-radius: 0.85rem;
  font-size: 0.92rem;
  line-height: 1.5;
  word-wrap: break-word;
}

.zi-bubble-user {
  background: var(--primary, #38B22D);
  color: #fff;
  border-bottom-right-radius: 0.25rem;
}

.zi-bubble-assistant {
  background: #f4f4f8;
  color: #3a3a45;
  border-bottom-left-radius: 0.25rem;
}

.zi-bubble-declined {
  background: #fff8e6;
  color: #6b5b1f;
  border: 1px solid #f0e0a8;
  border-bottom-left-radius: 0.25rem;
}

.zi-bubble-error {
  background: #fdecec;
  color: #9a3b3b;
  border: 1px solid #f3c8c8;
}

.zi-bubble-stage {
  background: #f4f4f8;
  color: #8a8a93;
  font-size: 0.85rem;
  font-style: italic;
  display: flex;
  align-items: center;
}

.zi-stage-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #b9b9c3;
  display: inline-block;
  margin-right: 3px;
  animation: zi-pulse 1.2s infinite ease-in-out;
}

.zi-stage-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.zi-stage-dot:nth-child(3) {
  animation-delay: 0.4s;
  margin-right: 8px;
}

@keyframes zi-pulse {
  0%, 80%, 100% { opacity: 0.3; }
  40% { opacity: 1; }
}

.zi-bubble ::v-deep p {
  margin-bottom: 0.5rem;
}

.zi-bubble ::v-deep p:last-child {
  margin-bottom: 0;
}

.zi-bubble ::v-deep ul, .zi-bubble ::v-deep ol {
  margin-bottom: 0.5rem;
  padding-left: 1.25rem;
}

.zi-bubble ::v-deep code {
  background: rgba(0, 0, 0, 0.06);
  padding: 0.1rem 0.3rem;
  border-radius: 0.25rem;
  font-size: 0.85em;
}

.zi-bubble ::v-deep pre {
  background: rgba(0, 0, 0, 0.06);
  padding: 0.6rem;
  border-radius: 0.4rem;
  overflow-x: auto;
}

.zi-bubble ::v-deep table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
}

.zi-bubble ::v-deep th,
.zi-bubble ::v-deep td {
  padding: 0.4rem 0.65rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  text-align: left;
}

.zi-bubble ::v-deep th {
  background: rgba(0, 0, 0, 0.05);
  font-weight: 600;
}

.zi-bubble ::v-deep tr:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

.zi-bubble ::v-deep a {
  color: var(--primary, #38B22D);
  text-decoration: underline;
}

.zi-citations {
  margin-top: 0.5rem;
  padding-top: 0.4rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.72rem;
  opacity: 0.65;
}

.zi-message-actions {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.3rem;
}

.zi-action-btn {
  border: none;
  background: transparent;
  color: #b9b9c3;
  padding: 0.2rem 0.35rem;
  border-radius: 0.3rem;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.zi-action-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #6e6b7b;
}

/* Unselected: outline only, neutral gray (inherited from .zi-action-btn).
   Selected: filled solid — feather icons are stroke-only outlines by
   default, so setting fill on the svg is what makes it read as "solid". */
.zi-like-btn.zi-action-active {
  color: #38B22D;
}

.zi-like-btn.zi-action-active svg {
  fill: #38B22D;
}

.zi-dislike-btn.zi-action-active {
  color: #ea5455;
}

.zi-dislike-btn.zi-action-active svg {
  fill: #ea5455;
}

.zi-input-area {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 0.85rem 1.25rem;
}

.zi-input {
  resize: none;
  border-radius: 0.6rem;
}

.zi-input-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.zi-char-counter {
  font-size: 0.75rem;
  color: #b9b9c3;
}

.zi-char-limit {
  color: #ea5455;
  font-weight: 600;
}

.zi-disclaimer {
  margin-top: 0.4rem;
  text-align: center;
  font-size: 0.7rem;
  color: #b9b9c3;
}

@media (max-width: 991px) {
  .zi-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 20;
    transform: translateX(-100%);
    transition: transform 0.2s ease;
    box-shadow: 2px 0 12px rgba(0, 0, 0, 0.15);
  }

  .zi-sidebar-open {
    transform: translateX(0);
  }

  .zi-bubble-wrap {
    max-width: 88%;
  }

  .zi-page {
    height: calc(100vh - 110px);
  }
}
</style>
